import { apiClient } from './client'

export interface Space {
  id: string
  name: string
  description?: string
  orgId: string
  deploymentId?: string
  deploymentIds?: string[]
  sessions?: SpaceListedSession[]
  userCount?: number
  sessionCount?: number
  ownerId?: string
  createdAt: string
  updatedAt: string
  status: 'active' | 'archived'
  /**
   * `session` when the person is not a member of the space but was invited
   * into some of its discussions (AGENT-862): `sessions` then holds only
   * those, and the space actions (new discussion, rename, delete) are not
   * theirs.
   */
  access?: 'session' | 'space'
}

/** A space with no deployment binding is usable by any Agent in the org. */
export function isSpaceUsableForDeployment(
  space: Space,
  deploymentId: string | null | undefined,
): boolean {
  if (!deploymentId) {
    return false
  }

  const scopedDeploymentIds = [
    ...(Array.isArray(space.deploymentIds) ? space.deploymentIds : []),
    ...(space.deploymentId ? [space.deploymentId] : []),
  ].filter(Boolean)

  if (scopedDeploymentIds.length === 0) {
    return true
  }

  return scopedDeploymentIds.includes(deploymentId)
}

/** Resolve the owning space before opening a session deep-link. */
export function findSpaceIdForSession(
  spaces: Space[],
  sessionId: string | null | undefined,
): string | null {
  if (!sessionId) {
    return null
  }

  return (
    spaces.find((space) =>
      (space.sessions || []).some((session) => session.id === sessionId),
    )?.id || null
  )
}

export interface SpaceListedSession {
  id: string
  name: string
  createdAt: string
  updatedAt: string
  lastActivityAt: string
  status?: 'active' | 'closed'
  /**
   * Application the session is bound to (`session.application_id`), absent
   * while unbound. A discussion belongs to one Application (AGENT-856).
   */
  applicationId?: string
}

/** Application id carried by a raw session payload, if any (AGENT-856). */
export function sessionApplicationId(session: Record<string, unknown> | null | undefined): string | undefined {
  const raw = session?.application_id ?? session?.applicationId
  const value = typeof raw === 'string' ? raw.trim() : ''
  return value || undefined
}

/**
 * A session is listed in an Application unless it is bound to another one
 * (AGENT-856: with a concierge shared by two Applications, their space is
 * shared too). An unbound session stays visible; it is bound on opening.
 */
export function sessionVisibleInApplication(
  session: Pick<SpaceListedSession, 'applicationId'>,
  applicationId: string | null | undefined,
): boolean {
  return !applicationId || !session.applicationId || session.applicationId === applicationId
}

export interface SpaceSession {
  id: string
  spaceId: string
  userId: string
  name: string
  createdAt: string
  updatedAt: string
  lastActivityAt: string
  status: 'active' | 'closed'
}

/** Prefer a human session name over empty / placeholder labels. */
export function preferSessionName(
  ...candidates: Array<string | null | undefined>
): string {
  for (const candidate of candidates) {
    const trimmed = candidate?.trim()
    if (trimmed && trimmed !== 'Session') {
      return trimmed
    }
  }
  return ''
}

export interface SpaceSessionOpenResponse {
  sessionId: string
  deploymentId?: string
  token: string
  url: string
  expiresAt?: string
  session?: SpaceSession
  space?: Space
}

function mapSpace(space: Record<string, any>): Space {
  const edgeDeployments = Array.isArray(space?.edges?.deployment)
    ? space.edges.deployment
    : Array.isArray(space?.deployment)
      ? space.deployment
      : space?.deployment
        ? [space.deployment]
      : []
  const deploymentIds = edgeDeployments
    .map((item: any) => String(item?.id ?? item?.deployment_id ?? item?.deploymentId ?? ''))
    .filter(Boolean)
  const directDeploymentId = String(
    space?.deployment_id ??
      space?.deploymentId ??
      space?.target_deployment_id ??
      space?.targetDeploymentId ??
      '',
  )
  const rawSessions = Array.isArray(space?.sessions)
    ? space.sessions
    : Array.isArray(space?.edges?.sessions)
      ? space.edges.sessions
      : []

  return {
    id: String(space?.id ?? ''),
    name: space?.name ?? 'Workspace',
    description: space?.description ?? '',
    orgId: String(
      space?.organization_id ??
        space?.organizationId ??
        space?.orgId ??
        '',
    ),
    deploymentId: directDeploymentId || deploymentIds[0] || undefined,
    deploymentIds: deploymentIds.length > 0 ? deploymentIds : undefined,
    sessions: rawSessions
      .map((item: any) => mapListedSession(item ?? {}))
      .filter((session) => Boolean(session.id)),
    userCount:
      space?.user_count ??
      space?.userCount ??
      space?.members?.length ??
      space?.edges?.users?.length ??
      undefined,
    sessionCount:
      space?.session_count ??
      space?.sessionCount ??
      rawSessions.length ??
      undefined,
    ownerId: space?.owner_id ?? space?.ownerId,
    createdAt:
      space?.created_at ?? space?.createdAt ?? new Date().toISOString(),
    updatedAt:
      space?.updated_at ?? space?.updatedAt ?? new Date().toISOString(),
    status: space?.status ?? 'active',
    access: space?.access === 'session' ? 'session' : undefined,
  }
}

function mapSession(session: Record<string, any>): SpaceSession {
  return {
    id: String(session?.id ?? session?.session_id ?? ''),
    spaceId: String(
      session?.space_id ?? session?.spaceId ?? '',
    ),
    userId: String(session?.user_id ?? session?.userId ?? session?.owner_id ?? ''),
    name: preferSessionName(session?.name, session?.title),
    createdAt:
      session?.created_at ?? session?.createdAt ?? new Date().toISOString(),
    updatedAt:
      session?.updated_at ?? session?.updatedAt ?? new Date().toISOString(),
    lastActivityAt:
      session?.last_activity_at ??
      session?.lastActivityAt ??
      session?.updated_at ??
      session?.updatedAt ??
      new Date().toISOString(),
    status: session?.status ?? 'active',
  }
}

function mapListedSession(session: Record<string, any>): SpaceListedSession {
  const applicationId = sessionApplicationId(session)
  return {
    ...(applicationId ? { applicationId } : {}),
    id: String(session?.id ?? session?.session_id ?? ''),
    name: preferSessionName(session?.name, session?.title) || 'Session',
    createdAt:
      session?.created_at ?? session?.createdAt ?? new Date().toISOString(),
    updatedAt:
      session?.updated_at ?? session?.updatedAt ?? new Date().toISOString(),
    lastActivityAt:
      session?.last_activity_at ??
      session?.lastActivityAt ??
      session?.updated_at ??
      session?.updatedAt ??
      new Date().toISOString(),
    status: session?.status ?? 'active',
  }
}

class SpacesAPI {
  async listMine(): Promise<Space[]> {
    const response = await apiClient.get<Record<string, any>>('/space/mine')
    const spaces = Array.isArray(response?.spaces) ? response.spaces : []
    return spaces.map((item) => mapSpace(item ?? {}))
  }

  async listByDeployment(deploymentId: string, organizationId?: string): Promise<Space[]> {
    const response = await apiClient.get<Record<string, any>>(
      `/space/deployment/${deploymentId}`,
      {
        params: organizationId ? { organization_id: organizationId } : undefined,
      },
    )
    const spaces = Array.isArray(response?.spaces) ? response.spaces : []
    return spaces.map((item) => mapSpace(item ?? {}))
  }

  /**
   * List spaces
   */
  async listSpaces(): Promise<Space[]> {
    return apiClient.get('/spaces')
  }

  /**
   * Get space details
   */
  async getSpace(spaceId: string): Promise<Space> {
    return apiClient.get(`/spaces/${spaceId}`)
  }

  /**
   * Create a space
   */
  async createSpace(data: {
    name: string
    description?: string
    deployment_id?: string
  }): Promise<Space> {
    const response = await apiClient.post<Record<string, any>>('/space', data)
    return mapSpace(response?.space ?? response)
  }

  async deleteSpace(spaceId: string): Promise<void> {
    return apiClient.delete(`/space/${spaceId}`)
  }

  /**
   * Create a chat session in a space
   */
  async createSession(spaceId: string): Promise<SpaceSession> {
    const response = await apiClient.post<Record<string, any>>(
      `/space/create_session/${spaceId}`,
      {},
    )
    return mapSession(response)
  }

  /**
   * Open a session and get LiveKit credentials
   */
  async openSession(
    sessionId: string,
  ): Promise<SpaceSessionOpenResponse> {
    const response = await apiClient.get<Record<string, any>>(
      `/space/open_session/${sessionId}`,
    )

    const session = response?.session ? mapSession(response.session) : undefined
    const space = response?.space ? mapSpace(response.space) : undefined

    return {
      sessionId: session?.id || String(response?.session_id ?? sessionId),
      deploymentId: response?.deployment_id ?? response?.deploymentId,
      token: response?.token ?? '',
      url: response?.url ?? '',
      expiresAt: response?.expires_at ?? response?.expiresAt,
      session,
      space,
    }
  }

  /**
   * Close a session
   */
  async closeSession(spaceId: string, sessionId: string): Promise<void> {
    return apiClient.delete(`/space/delete_session/${sessionId}`)
  }

  async updateSession(
    sessionId: string,
    data: { name?: string; description?: string },
  ): Promise<SpaceListedSession> {
    const response = await apiClient.put<Record<string, any>>(
      `/space/update_session/${sessionId}`,
      data,
    )
    return mapListedSession(response)
  }

  /**
   * List sessions in a space
   */
  async listSessions(
    spaceId: string,
    limit: number = 50,
    offset: number = 0,
  ): Promise<{ items: SpaceSession[]; total: number }> {
    const response = await apiClient.get<any[]>(`/space/${spaceId}/sessions`, {
      params: { limit, offset },
    })

    const items = Array.isArray(response) ? response.map((item) => mapSession(item ?? {})) : []
    return { items, total: items.length }
  }

  async listMySessions(
    spaceId: string,
    limit: number = 50,
    offset: number = 0,
  ): Promise<{ items: SpaceSession[]; total: number }> {
    const response = await apiClient.get<any[]>(`/space/${spaceId}/sessions/mine`, {
      params: { limit, offset },
    })

    const items = Array.isArray(response)
      ? response.map((item) => mapSession(item ?? {}))
      : []
    return { items, total: items.length }
  }
}

export const spacesAPI = new SpacesAPI()
